import { Chip, IconButton, Paper, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { Clock, MessageSquarePlus, Plus, X } from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';
import { BuildingLog } from './BuildingLog';
import { ChatInput } from './ChatInput';
import { ChatMessage, messageIsWriteAction } from './ChatMessage';
import { ConversationList } from './ConversationList';
import { FeedbackDialog } from './FeedbackDialog';
import type { CopilotAttachment } from './useCopilotChat';
import { useMessageFeedback } from './useMessageFeedback';
import type { ToolAction } from './useCopilotChat';

const useStyles = makeStyles((theme) => ({
  root: {
    // Right-pinned docked sidebar: starts below the top nav, full height
    // to the bottom, flush against the right edge. z-index sits BELOW the
    // top nav (z-index 100 in TopNav.tsx) so the nav stays clickable.
    // RoutesPushable (App.tsx) pushes content left on /experiences/:id so
    // the sidebar doesn't overlap.
    position: 'fixed',
    top: 56, // top nav height — keep in sync with TopNav padding+content
    right: 0,
    bottom: 0,
    width: 400,
    maxWidth: '100vw',
    display: 'flex',
    flexDirection: 'column',
    zIndex: 99,
    // Rounded top corners (panel is flush to the viewport bottom).
    // `overflow: hidden` clips the gradient header to match.
    borderRadius: '12px 12px 0 0',
    overflow: 'hidden',
    boxShadow: '-2px 0 12px rgba(0,0,0,0.12)',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    height: 56,
    padding: theme.spacing(0, 2),
    background: theme.dado.copilot.gradientDiagonal,
    color: '#fff',
    flexShrink: 0,
  },
  headerTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    fontSize: 14,
    fontWeight: 600,
  },
  headerActions: {
    display: 'flex',
    alignItems: 'center',
    gap: 4,
  },
  messages: {
    flex: 1,
    overflowY: 'auto',
    padding: theme.spacing(2),
    background: '#F8FAFB',
  },
  empty: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    color: theme.palette.text.secondary,
    textAlign: 'center',
  },
  historyPanel: {
    flex: 1,
    overflowY: 'auto',
    background: '#F8FAFB',
  },
}));

interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  createdAt: Date;
  /** Agent-suggested quick-reply labels for this turn (Build it / Adjust). */
  quickReplies?: string[];
}

interface ConversationSummary {
  id: string;
  title: string | null;
  messageCount: number;
  lastMessageAt: string;
  createdAt: string;
}

interface ChatWindowProps {
  messages: Message[];
  isLoading: boolean;
  onSend: (message: string, attachments?: CopilotAttachment[]) => void;
  onClose: () => void;
  onNewChat: () => void;
  // History props
  conversations: ConversationSummary[];
  isLoadingHistory: boolean;
  activeConversationId: string | null;
  onLoadConversation: (id: string) => void;
  // Page context
  pageContext?: { templateId?: string } | null;
  // Building state — when present, replaces the chat body with a
  // structured log. Cleared via `onBuildingDone` after the run finishes.
  buildingActions?: ToolAction[];
  onBuildingDone?: () => void;
  // Feedback — submit handler (from useCopilotChat) + whether a plan has
  // already been built this conversation (gates the Build it/Adjust chips).
  submitFeedback?: (args: {
    source: 'message' | 'general';
    rating?: 'up' | 'down';
    comment?: string;
    category?: string;
    messageId?: string;
    messageIndex?: number;
  }) => Promise<{ ok: boolean }>;
  hasWriteActionOccurred?: boolean;
}

export const ChatWindow = React.forwardRef<HTMLDivElement, ChatWindowProps>(({
  messages,
  isLoading,
  onSend,
  onClose,
  onNewChat,
  conversations,
  isLoadingHistory,
  activeConversationId,
  onLoadConversation,
  pageContext,
  buildingActions,
  onBuildingDone,
  submitFeedback,
  hasWriteActionOccurred,
}, ref) => {
  const classes = useStyles();
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const isFirstScroll = useRef(true);
  const [showHistory, setShowHistory] = useState(false);
  const buildingActive = !!(buildingActions && buildingActions.length > 0);

  const [feedbackDialogOpen, setFeedbackDialogOpen] = useState(false);

  // Per-message feedback state + handlers (shared hook). Keyed by message
  // id (assistant-${Date.now()} live / server id on reload).
  const { feedbackByMsg, handleRate, handleToggleComment, handleSubmitComment, reset } =
    useMessageFeedback(submitFeedback);

  // Reset per-message feedback when the conversation changes (new chat /
  // loaded a different conversation) — old keys won't match new messages.
  useEffect(() => {
    reset();
  }, [activeConversationId, reset]);

  // "Build it"/"Adjust" must vanish once a plan has been implemented.
  // Primary signal: hasWriteActionOccurred (from handleActions in index).
  // Fallback (survives ai_loadConversation rehydrate): scan messages for a
  // write-action result.
  const planBuilt =
    !!hasWriteActionOccurred ||
    messages.some((m) => m.role === 'assistant' && messageIsWriteAction(m.content));

  // Re-arm the instant (non-animated) scroll whenever the conversation
  // changes. Opening the window or switching/loading a conversation from
  // history populates `messages` in one shot — that should JUMP to the
  // bottom, never smooth-scroll down through the freshly-loaded history.
  // New messages within the SAME conversation leave isFirstScroll false, so
  // they keep the smooth scroll-into-view. Declared BEFORE the scroll effect
  // so it runs first on a render where both conversation + messages change.
  useEffect(() => {
    isFirstScroll.current = true;
  }, [activeConversationId]);

  useEffect(() => {
    // First scroll after (re)arming = instant jump (behavior: 'auto'), not
    // smooth. The sidebar is already sliding in and content is pushing left;
    // an extra smooth-scroll on top reads as visual chaos. Subsequent message
    // arrivals (sidebar already settled) keep the smooth scroll for context.
    if (buildingActive) return;
    messagesEndRef.current?.scrollIntoView({
      behavior: isFirstScroll.current ? 'auto' : 'smooth',
    });
    isFirstScroll.current = false;
  }, [messages, buildingActive]);

  return (
    <Paper ref={ref} elevation={8} className={classes.root}>
      <div className={classes.header}>
        <div className={classes.headerTitle}>
          <svg
            width="16"
            height="16"
            viewBox="0 0 12 12"
            fill="currentColor"
            aria-hidden="true"
          >
            <path d="M6 0L7.2 4.8L12 6L7.2 7.2L6 12L4.8 7.2L0 6L4.8 4.8L6 0Z" />
          </svg>
          <Typography variant="subtitle1" component="span" style={{ fontWeight: 600 }}>
            Assistant
          </Typography>
          <span
            style={{
              alignSelf: 'flex-start',
              fontSize: 7,
              fontWeight: 700,
              letterSpacing: '0.06em',
              padding: '1.5px 4px',
              borderRadius: 2,
              background: 'rgb(4, 80, 156)',
              color: 'rgb(255, 255, 255)',
              lineHeight: 1,
            }}
          >
            BETA
          </span>
        </div>
        <div className={classes.headerActions}>
          <IconButton
            size="small"
            onClick={() => setFeedbackDialogOpen(true)}
            style={{ color: 'inherit' }}
            aria-label="Give feedback"
            title="Give feedback"
          >
            <MessageSquarePlus size={18} />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => setShowHistory(!showHistory)}
            style={{ color: 'inherit' }}
            aria-label="Conversation history"
            title="Conversation history"
          >
            <Clock size={18} />
          </IconButton>
          <IconButton
            size="small"
            onClick={onNewChat}
            style={{ color: 'inherit' }}
            aria-label="New conversation"
            title="New conversation"
          >
            <Plus size={18} />
          </IconButton>
          <IconButton
            size="small"
            onClick={onClose}
            style={{ color: 'inherit' }}
            aria-label="Close chat"
          >
            <X size={18} />
          </IconButton>
        </div>
      </div>
      {buildingActive && onBuildingDone ? (
        <BuildingLog actions={buildingActions!} onDone={onBuildingDone} />
      ) : showHistory ? (
        <div className={classes.historyPanel}>
          <ConversationList
            conversations={conversations}
            isLoading={isLoadingHistory}
            activeConversationId={activeConversationId}
            onSelect={(id) => {
              onLoadConversation(id);
              setShowHistory(false);
            }}
            onBack={() => setShowHistory(false)}
          />
        </div>
      ) : (
        <>
          <div className={classes.messages}>
            {messages.length === 0 ? (
              <div className={classes.empty}>
                <div style={{ padding: 16 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    {pageContext?.templateId ? 'Working on this template' : 'How can I help?'}
                  </Typography>
                  <Typography variant="body2" color="textSecondary" style={{ marginBottom: 16 }}>
                    {pageContext?.templateId
                      ? 'I can see this template. Ask me to add tasks, review it, or make changes.'
                      : 'Ask about your experiences, templates, or users. I can also help you build new ones.'}
                  </Typography>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, justifyContent: 'center' }}>
                    {(pageContext?.templateId
                      ? [
                          'Review this template and suggest improvements',
                          'Add a task for the manager',
                          'Add a welcome message on Day 1',
                          'What subtask types can I use?',
                          'Set up audiences for different departments',
                          'Show me the validation checklist',
                        ]
                      : [
                          'I want to create a new experience',
                          'What templates do I have?',
                          'Show me something from the library',
                          // 'How are my onboardings going?',
                          // 'Who is behind on their tasks?',
                        ]
                    ).map((prompt) => (
                      <Chip
                        key={prompt}
                        label={prompt}
                        variant="outlined"
                        size="small"
                        clickable
                        onClick={() => onSend(prompt)}
                        style={{ marginBottom: 4 }}
                      />
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              messages.map((msg, idx) => {
                // Quick-reply chips ("Build it" / "Adjust") are driven by the
                // AGENT, not a page heuristic — it appends a hidden marker only
                // when it has presented a ready-to-build plan (stripped + parsed
                // server-side into `msg.quickReplies`). We render them on the
                // latest idle assistant turn only, and never once a write has
                // landed (`planBuilt` is a defensive belt-and-suspenders guard
                // in case the agent over-emits the marker post-build).
                const isLastIdleAssistant =
                  idx === messages.length - 1 &&
                  msg.role === 'assistant' &&
                  !isLoading;
                const replyLabels =
                  isLastIdleAssistant && !planBuilt ? msg.quickReplies : undefined;
                const showQuickReplies = !!(replyLabels && replyLabels.length > 0);
                return (
                  <ChatMessage
                    key={msg.id}
                    role={msg.role}
                    content={msg.content}
                    quickReplies={
                      showQuickReplies
                        ? replyLabels!.map((label, i) => ({
                            label,
                            variant: i === 0 ? 'primary' : 'secondary',
                          }))
                        : undefined
                    }
                    onQuickReply={showQuickReplies ? onSend : undefined}
                    // Feedback icons show under every assistant TEXT message
                    // (ChatMessage early-returns for action cards, so they
                    // won't appear there). Independent of planBuilt.
                    showFeedback={msg.role === 'assistant'}
                    feedback={feedbackByMsg[msg.id]}
                    onRate={(rating) => handleRate(msg.id, idx, rating, msg.content)}
                    onToggleComment={() => handleToggleComment(msg.id)}
                    onSubmitComment={(text) => handleSubmitComment(msg.id, idx, text, msg.content)}
                  />
                );
              })
            )}
            {isLoading && <ChatMessage role="assistant" content="" isLoading />}
            <div ref={messagesEndRef} />
          </div>
          <ChatInput onSend={onSend} disabled={isLoading} />
        </>
      )}
      <FeedbackDialog
        open={feedbackDialogOpen}
        onClose={() => setFeedbackDialogOpen(false)}
        onSubmit={(a) =>
          submitFeedback
            ? submitFeedback({ source: 'general', comment: a.comment, category: a.category })
            : Promise.resolve({ ok: false })
        }
      />
    </Paper>
  );
});
ChatWindow.displayName = 'ChatWindow';
